import { Bell, CalendarDays, ChevronLeft } from "lucide-react";
import { useGetIdentity, useTranslate } from "ra-core";
import { useLocation } from "react-router";

import { cn } from "@/lib/utils";
import "@/styles/topbar-back.css";

import { UserAvatar } from "../misc/UserAvatar";
import { GlobalSearchButton } from "../search/GlobalSearchButton";
import { hasTopBarBack, useMobileBack } from "./mobileBack";
import { useMobileChrome } from "./MobileChrome";
import type { MobileWindowId } from "./MobileWindowState";

/** Round glass control of the floating top row, one size for all of them. */
export const GLASS_ROUND_BUTTON =
  "liquid-glass glass-press pointer-events-auto flex size-[var(--mobile-control)] shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50";

/**
 * Floating glass row present on every mobile screen: account avatar, the
 * search pill and the notifications/calendar shortcuts, all one height. These
 * are the only copies of those commands on a phone.
 *
 * On a secondary page (a module reached from the "Más" hub) a back chevron
 * takes the avatar's place (see mobileBack.ts); the root tabs keep it.
 *
 * The avatar, the bell and the calendar (the full one: week / month /
 * agenda) each open a full-screen window that grows out of them
 * (MorphWindow); its close button lands on the button that opened it (found by `data-morph-trigger`), which fades out under it.
 */
export const MobileTopBar = () => {
  const translate = useTranslate();
  const { activeWindow, openWindow } = useMobileChrome();
  const { identity } = useGetIdentity();
  const { pathname } = useLocation();
  const showBack = hasTopBarBack(pathname);
  const goBack = useMobileBack();
  const trigger = (id: MobileWindowId) => ({
    type: "button" as const,
    "data-morph-trigger": id,
    "aria-haspopup": "dialog" as const,
    "aria-expanded": activeWindow === id,
    onClick: () => openWindow(id),
  });

  return (
    <header className="pointer-events-none fixed inset-x-0 top-[var(--mobile-chrome-top)] z-40 flex items-center gap-2 px-[var(--mobile-chrome-inline)]">
      {showBack ? (
        <button
          type="button"
          // Keyed per page so its pop-in plays on each secondary page.
          key={pathname}
          data-topbar-back=""
          className={cn(GLASS_ROUND_BUTTON, "topbar-back")}
          aria-label={translate("crm.mobile.back")}
          onClick={goBack}
        >
          <ChevronLeft
            className="size-[1.375rem] -translate-x-px"
            strokeWidth={2.25}
          />
        </button>
      ) : (
        <button
          {...trigger("account")}
          className="morph-trigger glass-press pointer-events-auto size-[var(--mobile-control)] shrink-0 rounded-full shadow-[0_1px_2px_rgb(16_24_40/0.08),0_4px_12px_rgb(16_24_40/0.08)] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          aria-label={translate("crm.mobile.account")}
        >
          <UserAvatar
            src={identity?.avatar}
            name={identity?.fullName}
            className="size-full text-[13px]"
          />
        </button>
      )}
      <GlobalSearchButton variant="pill" />
      <button
        {...trigger("alerts")}
        className={cn(GLASS_ROUND_BUTTON, "morph-trigger")}
        aria-label={translate("crm.mobile.notifications")}
      >
        <Bell className="size-[1.125rem]" strokeWidth={2} />
      </button>
      <button
        {...trigger("agenda")}
        className={cn(GLASS_ROUND_BUTTON, "morph-trigger")}
        aria-label={translate("crm.mobile.calendar_window.open")}
      >
        <CalendarDays className="size-[1.125rem]" strokeWidth={2} />
      </button>
    </header>
  );
};
